import { z } from "zod";
import { WEBHOOK_EVENT_KEYS } from "@yazamco/shared-types";

export const WEBHOOK_FORMATS = ["json", "slack", "discord", "teams"] as const;
export const webhookFormatSchema = z.enum(WEBHOOK_FORMATS);
export const NOTIFICATION_TRANSPORTS = ["http", "email"] as const;
export const NOTIFICATION_TEMPLATE_IDS = [
  "generic",
  "operational-alert",
  "security-event",
  "container-update",
  "resource-threshold",
  "minimal",
  "detailed",
] as const;

const eventKeySet = new Set<string>(WEBHOOK_EVENT_KEYS);
const eventsSchema = z
  .array(z.string().refine((key) => eventKeySet.has(key), "unknown webhook event key"))
  .max(200)
  .transform((keys) => [...new Set(keys)]);
const percent = z.coerce.number().int().min(1).max(100);
const thresholdsSchema = z.object({ disk: percent, cpu: percent, memory: percent }).partial();

const FORBIDDEN_VARIABLE = /(?:secret|password|passwd|token|api[_-]?key|credential|smtp|database|private[_-]?key|connection[_-]?string)/i;
const PROTECTED_VARIABLES = new Set([
  "schemaVersion",
  "event",
  "timestamp",
  "severity",
  "title",
  "summary",
  "source",
  "actor",
  "environment",
  "data",
  "links",
  "variables",
]);

export function isSafeNotificationVariableName(key: string): boolean {
  return (
    /^[A-Za-z][A-Za-z0-9_]{0,39}$/.test(key) &&
    !FORBIDDEN_VARIABLE.test(key) &&
    !PROTECTED_VARIABLES.has(key)
  );
}

const variableValue = z.union([
  z.string().max(1000),
  z.number().finite(),
  z.boolean(),
]);
const variablesSchema = z
  .record(z.string(), variableValue)
  .superRefine((value, ctx) => {
    const entries = Object.entries(value);
    if (entries.length > 30) {
      ctx.addIssue({ code: "custom", message: "at most 30 custom variables are allowed" });
    }
    for (const [key, item] of entries) {
      if (!isSafeNotificationVariableName(key)) {
        ctx.addIssue({ code: "custom", path: [key], message: "invalid, protected, or secret-like variable name" });
      }
      if (String(item).length > 1000) {
        ctx.addIssue({ code: "custom", path: [key], message: "variable value is too long" });
      }
    }
  });

const email = z
  .string()
  .trim()
  .max(320)
  .email()
  .refine((value) => !/[\r\n]/.test(value), "email address contains a line break");
const recipientList = z.array(email).max(50);
const recipientsSchema = z
  .object({ to: recipientList, cc: recipientList, bcc: recipientList })
  .partial();
const templateText = z.string().max(100_000).nullable();
const customTemplatesSchema = z
  .object({
    httpJson: templateText.optional(),
    emailSubject: z.string().max(500).refine((v) => !/[\r\n]/.test(v), "subject contains a line break").nullable().optional(),
    emailHtml: templateText.optional(),
    emailText: templateText.optional(),
  })
  .strict();

/**
 * Validate JSON structure without evaluating placeholders. A quoted sentinel is
 * used so placeholders remain data, never JSON syntax or executable content.
 */
export function isValidNotificationJsonTemplate(value: string): boolean {
  try {
    JSON.parse(value.replace(/\{\{\s*[A-Za-z][A-Za-z0-9_.]*\s*\}\}/g, "__value__"));
    return true;
  } catch {
    return false;
  }
}

const baseDefinitionSchema = z
  .object({
    name: z.string().trim().min(1, "a name is required").max(120),
    transport: z.enum(NOTIFICATION_TRANSPORTS).optional(),
    url: z.string().trim().max(2000).optional(),
    format: webhookFormatSchema.optional(),
    templateId: z.enum(NOTIFICATION_TEMPLATE_IDS).optional(),
    customTemplates: customTemplatesSchema.optional(),
    variables: variablesSchema.optional(),
    recipients: recipientsSchema.optional(),
    events: eventsSchema.optional(),
    thresholds: thresholdsSchema.optional(),
    enabled: z.boolean().optional(),
    secret: z.string().max(500).nullable().optional(),
  })
  .superRefine((value, ctx) => {
    const transport = value.transport ?? "http";
    if (transport === "http") {
      if (!value.url) {
        ctx.addIssue({ code: "custom", path: ["url"], message: "an HTTP URL is required" });
      } else {
        try {
          const url = new URL(value.url);
          if (url.protocol !== "http:" && url.protocol !== "https:") throw new Error();
        } catch {
          ctx.addIssue({ code: "custom", path: ["url"], message: "must be a valid http(s) URL" });
        }
      }
    } else if ((value.recipients?.to?.length ?? 0) === 0) {
      ctx.addIssue({ code: "custom", path: ["recipients", "to"], message: "at least one To recipient is required" });
    }
    const json = value.customTemplates?.httpJson;
    if (json && !isValidNotificationJsonTemplate(json)) {
      ctx.addIssue({ code: "custom", path: ["customTemplates", "httpJson"], message: "custom HTTP template must be valid JSON with quoted placeholders" });
    }
  });

export const webhookDefinitionSchema = baseDefinitionSchema;
export const notificationPreviewSchema = baseDefinitionSchema.and(
  z.object({
    sampleEventKey: z.string().refine((key) => eventKeySet.has(key), "unknown webhook event key"),
  }),
);

export type WebhookDefinitionInput = z.infer<typeof webhookDefinitionSchema>;
export type NotificationPreviewInput = z.infer<typeof notificationPreviewSchema>;

export const WEBHOOK_DELIVERY_STATUSES = ["success", "failed"] as const;
export const WEBHOOK_EVENT_TYPES = ["test", "update", "replay", "event"] as const;
export const webhookDeliveryQuerySchema = z.object({
  webhookId: z.string().trim().max(64).optional(),
  status: z.enum(WEBHOOK_DELIVERY_STATUSES).optional(),
  type: z.enum(WEBHOOK_EVENT_TYPES).optional(),
  search: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});
export type WebhookDeliveryQueryInput = z.infer<typeof webhookDeliveryQuerySchema>;
